import { createContext, useContext, useEffect, useState } from "react";
import { fetchData } from "../../../lib/dashboard/data";
import useSWR from "swr";
import { sendData } from "../../../lib/dashboard/actions";
import { useDomesticMarketProductPriceChart } from "./DomesticMarketProductPriceChartContext";

const DomesticMarketProductPriceChartCategoryContext = createContext();

const DomesticMarketProductPriceChartCategoryProvider = ({ children }) => {
  const { setIsFavoritesModal } = useDomesticMarketProductPriceChart();

  const [inputValues, setInputValues] = useState({
    industry: null,
    field: null,
    group: null,
    company: null,
    commodity: null,
  });

  const [categoryChartData, setCategoryChartData] = useState({
    data: [],
    isLoading: false,
  });

  const industries = useSWR("/domestic/category/industry", fetchData, {
    dedupingInterval: 60 * 60 * 1000,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const fields = useSWR(
    inputValues.industry
      ? `/domestic/category/field/${inputValues.industry.id}`
      : null,
    fetchData,
    {
      dedupingInterval: 60 * 60 * 1000,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );
  const groups = useSWR(
    inputValues.field
      ? `/domestic/category/group/${inputValues.field.id}`
      : null,
    fetchData,
    {
      dedupingInterval: 60 * 60 * 1000,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const companies = useSWR(
    inputValues.group
      ? `/domestic/category/company/${inputValues.group.id}`
      : null,
    fetchData,
    {
      dedupingInterval: 60 * 60 * 1000,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );
  const commodities = useSWR(
    inputValues.company
      ? `/domestic/category/commodity/${inputValues.company.id}/${inputValues.group.id}`
      : null,
    fetchData,
    {
      dedupingInterval: 60 * 60 * 1000,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const handleShowChart = async (body) => {
    setCategoryChartData((prev) => ({ ...prev, isLoading: true }));
    const res = await sendData(body, "/domestic/category/price-chart");
    if (res?.status === 200)
      setCategoryChartData({ data: res.data, isLoading: false });
    setCategoryChartData((prev) => ({ ...prev, isLoading: false }));
  };

  const handleSelectedCategoryFavorite = async (favorite) => {
    const {
      industry,
      industry_id,
      field,
      field_id,
      commodity,
      commodity_id,
      company,
      company_id,
      group,
      group_id,
    } = favorite;

    setInputValues((prev) => ({
      ...prev,
      industry: { id: industry_id, name: industry },
      field: { id: field_id, name: field },
      group: group && { id: group_id, name: group },
      company: company && { id: company_id, name: company },
      commodity: commodity && { id: commodity_id, name: commodity },
    }));

    await handleShowChart({
      industry_id,
      field_id,
      company_id,
      group_id,
      commodity_id,
    });

    setIsFavoritesModal(false);
  };

  return (
    <DomesticMarketProductPriceChartCategoryContext.Provider
      value={{
        inputValues,
        setInputValues,
        industries,
        fields,
        companies,
        groups,
        commodities,
        categoryChartData: categoryChartData.data,
        isCategoryChartLoading: categoryChartData.isLoading,
        setCategoryChartData,
        handleShowChart,
        handleSelectedCategoryFavorite,
      }}
    >
      {children}
    </DomesticMarketProductPriceChartCategoryContext.Provider>
  );
};

export default DomesticMarketProductPriceChartCategoryProvider;

export const useDomesticMarketProductPriceChartCategory = () => {
  return useContext(DomesticMarketProductPriceChartCategoryContext);
};
